import { createSelector } from 'reselect';
const getFilters = (state) => state.filters;
const getProducts = (state) => state.liquors.byId;
const getIds = (state) => state.liquors.ids;
const getDepartmentFilter = (state) => state.filters.department;
const getSearchTerm = (state) => state.filters.search;
const getSort = (state) => state.filters.sort;
export const filterIsActive = (filters, filter) => filters[filter].length > 0;

// Current model: all products => visibleProducts (based on department) => searchedProducts => filteredProducts => sort => pagination

const PRODUCTS_PER_PAGE = 30;
const filtersList = ['category', 'brand', 'varietal', 'type', 'region', 'size'];

const getVisibleProducts = createSelector(
    [getProducts, getIds, getDepartmentFilter],
    (products, ids, department) => {
        let result = ids.map((id) => products[id]);
        if (department) {
            result = result.filter(
                (product) => product.department === department
            );
        }
        return result;
    }
);

const getSearchedProducts = createSelector(
    [getVisibleProducts, getSearchTerm],
    (products, searchTerm) => {
        return products.filter((product) =>
            matchSearchTerm(searchTerm, product)
        );
    }
);

const getFilteredProducts = createSelector(
    [getFilters, getSearchedProducts],
    (filters, products) => {
        return products.filter((product) => {
            return (
                matchFilters(filters, product) &&
                // check price range
                (filters.price.length
                    ? product.salePrice >= filters.price[0] &&
                      product.salePrice <= filters.price[1]
                    : true)
            );
        });
    }
);

const getFilteredProductsSorted = createSelector(
    [getSort, getFilteredProducts],
    (sort, products) => {
        let sortFunction = ((sort) => {
            switch (sort) {
                case 'A-Z':
                    return (a, b) => a.name.localeCompare(b.name);
                case 'Z-A':
                    return (a, b) => b.name.localeCompare(a.name);
                case 'Low-High':
                    return (a, b) => a.salePrice - b.salePrice;
                case 'High-Low':
                    return (a, b) => b.salePrice - a.salePrice;
                default:
                    return undefined;
            }
        })(sort);
        return products.sort(sortFunction);
    }
);

export const getFilteredProductsWithPagination = createSelector(
    [getFilteredProductsSorted],
    (products) => {
        const result = [];
        let temp = [];
        for (let i = 0; i < products.length; i++) {
            temp.push(products[i]);
            if (i % PRODUCTS_PER_PAGE === PRODUCTS_PER_PAGE - 1) {
                result.push(temp);
                temp = [];
            }
        }
        if (temp.length) {
            result.push(temp);
        }
        return result;
    }
);

const getFilteredProductsExcludingFilter = (
    excludedFilter,
    filters,
    searchedProducts
) => {
    return searchedProducts.filter((product) => {
        return (
            matchFiltersExcludeFilter(filters, product, excludedFilter) &&
            (filters.price.length
                ? product.salePrice >= filters.price[0] &&
                  product.salePrice <= filters.price[1]
                : true)
        );
    });
};

const sortObject = (obj) => {
    const ordered = {};
    Object.keys(obj)
        .sort()
        .forEach((key) => {
            ordered[key] = obj[key];
        });
    return ordered;
};

export const getAvailableFilters = createSelector(
    [getSearchedProducts, getFilteredProducts, getFilters],
    (searchedProducts, filteredProducts, filters) => {
        const availableFilters = {};
        if (filters.department) {
            const activeFilters = [];
            const inactiveFilters = [];
            filtersList.forEach((filter) => {
                if (filterIsActive(filters, filter)) {
                    activeFilters.push(filter);
                } else {
                    inactiveFilters.push(filter);
                }
            });
            filteredProducts.forEach((product) => {
                inactiveFilters.forEach((property) => {
                    addToDictionary(property, availableFilters, product);
                });
            });
            activeFilters.forEach((filter) => {
                getFilteredProductsExcludingFilter(
                    filter,
                    filters,
                    searchedProducts
                ).forEach((product) => {
                    addToDictionary(filter, availableFilters, product);
                });
            });
        }
        for (let filter in availableFilters) {
            availableFilters[filter] = sortObject(availableFilters[filter]);
        }
        return availableFilters;
    }
);

const matchSearchTerm = (searchTerm, obj) => {
    if (searchTerm) {
        for (let key in obj) {
            if (key !== 'img') {
                if (
                    obj[key]
                        .toString()
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())
                ) {
                    return true;
                }
            }
        }
        return false;
    } else {
        return true;
    }
};

const matchFilters = (filters, product) => {
    for (let filter of filtersList) {
        if (
            filters[filter].length &&
            !filters[filter].includes(product[filter])
        ) {
            return false;
        }
    }
    return true;
};

const matchFiltersExcludeFilter = (filters, product, filterToExclude) => {
    for (let filter of filtersList) {
        if (
            filter !== filterToExclude &&
            filters[filter].length &&
            !filters[filter].includes(product[filter])
        ) {
            return false;
        }
    }
    return true;
};

const addToDictionary = (property, dict, obj) => {
    const value = obj[property];
    if (value) {
        if (!dict[property]) {
            dict[property] = {};
        }
        if (dict[property][value]) {
            dict[property][value]++;
        } else {
            dict[property][value] = 1;
        }
    }
};

export const getFiltersForCheckBox = createSelector([getFilters], (filters) => {
    const filtersForCheckBox = {};
    filtersList.forEach((key) => {
        filtersForCheckBox[key] = filters[key];
    });
    return filtersForCheckBox;
});

export const getProduct = (state, id) => state.liquors.byId[id];
